import React from 'react';
import cn from 'classnames';
import { DropdownButton, MenuItemProps, Portal } from '@odin-labs/components';
import {
  ExpandIcon,
  ObjectGroupIcon,
  PauseIcon,
  PlayIcon,
  VolumeHighIcon,
  VolumeSlashIcon,
} from 'components/icons';
import { PlayerControlsProps } from './types';

const PortalGroup = Portal.Group;

const PLAYBACK_SPEED_OPTIONS: MenuItemProps[] = [
  { value: 0.5, text: '0.5×' },
  { value: 1, text: '1×' },
  { value: 1.25, text: '1.25×' },
  { value: 1.5, text: '1.5×' },
  { value: 2, text: '2×' },
];

export function PlayerControls(props: PlayerControlsProps): React.ReactElement {
  const {
    containerRef,
    videoRef,
    isPlaying,
    isMuted,
    volumeLevel,
    currentPlaybackRate = 1,
    isPiP,
    isVisible,
    togglePlay,
    onMutedChange,
    onVolumeChange,
    onPlaybackRateChange,
    onPiPChange,
  } = props;

  const prevVolumeRef = React.useRef<number>(volumeLevel || 1);

  const toggleMuteWithRestore = React.useCallback((): void => {
    if (isMuted) {
      if (volumeLevel === 0) {
        onVolumeChange(prevVolumeRef.current);
      }
      onMutedChange(false);
    } else {
      onMutedChange(true);
    }
  }, [isMuted, volumeLevel, onMutedChange, onVolumeChange]);

  const handleVolumeChange = React.useCallback(
    (ev: React.ChangeEvent<HTMLInputElement>): void => {
      const newVolume = parseFloat(ev.target.value);
      onVolumeChange(newVolume);
      if (newVolume > 0) {
        prevVolumeRef.current = newVolume;
        onMutedChange(false);
      } else {
        onMutedChange(true);
      }
    },
    [onVolumeChange, onMutedChange],
  );

  const handleSpeedChange = React.useCallback(
    (value: number): void => {
      onPlaybackRateChange(value);
    },
    [onPlaybackRateChange],
  );

  const togglePiP = React.useCallback(async (): Promise<void> => {
    if (!videoRef.current) return;
    try {
      if (document.pictureInPictureElement) {
        await document.exitPictureInPicture();
        onPiPChange(false);
      } else if (document.pictureInPictureEnabled) {
        await videoRef.current.requestPictureInPicture();
        onPiPChange(true);
      }
    } catch {
      onPiPChange(!!document.pictureInPictureElement);
    }
  }, [videoRef, onPiPChange]);

  const toggleFullscreen = React.useCallback(async (): Promise<void> => {
    const el = containerRef.current;
    if (!el) return;
    try {
      if (document.fullscreenElement) {
        await document.exitFullscreen();
      } else {
        await el.requestFullscreen();
      }
    } catch {
      // Fullscreen can be rejected due to permissions, user gesture constraints,
      // or unsupported environments — safe to swallow as the UI will reflect actual state
    }
  }, [containerRef]);

  return (
    <div
      aria-hidden={!isVisible}
      onClick={(e): void => {
        // Prevent clicks on the controls bar from bubbling up to the player container,
        // which would unintentionally toggle play/pause
        e.stopPropagation();
      }}
      className={cn(
        'odin-absolute odin-bottom-0 odin-left-0 odin-right-0',
        'odin-flex odin-items-center odin-gap-3 odin-px-3 odin-py-2',
        'odin-bg-black/50 odin-transition-opacity odin-duration-300',
        isVisible
          ? 'odin-opacity-100'
          : 'odin-opacity-0 odin-pointer-events-none',
      )}
    >
      {/* Play / Pause */}
      <button
        type="button"
        onClick={togglePlay}
        aria-label={isPlaying ? 'Pause' : 'Play'}
        title={isPlaying ? 'Pause' : 'Play'}
        className="odin-text-white hover:odin-text-gray-300"
      >
        {isPlaying ? (
          <PauseIcon className="odin-h-4 odin-w-4" />
        ) : (
          <PlayIcon className="odin-h-4 odin-w-4" />
        )}
      </button>

      {/* Mute + Volume slider */}
      <button
        type="button"
        onClick={toggleMuteWithRestore}
        aria-label={isMuted || volumeLevel === 0 ? 'Unmute' : 'Mute'}
        title={isMuted || volumeLevel === 0 ? 'Unmute' : 'Mute'}
        className="odin-text-white hover:odin-text-gray-300"
      >
        {isMuted || volumeLevel === 0 ? (
          <VolumeSlashIcon className="odin-h-4 odin-w-4" />
        ) : (
          <VolumeHighIcon className="odin-h-4 odin-w-4" />
        )}
      </button>
      <input
        type="range"
        min={0}
        max={1}
        step={0.05}
        value={isMuted ? 0 : volumeLevel}
        onChange={handleVolumeChange}
        aria-label="Volume"
        className="odin-w-20 odin-accent-white odin-cursor-pointer"
      />

      <div className="odin-flex-1" />

      {/* Playback speed */}
      <PortalGroup target={containerRef}>
        <DropdownButton
          text={`${currentPlaybackRate}×`}
          menuItems={PLAYBACK_SPEED_OPTIONS}
          onItemSelect={handleSpeedChange}
          className="odin-h-5.5"
          size="xs"
          theme="white"
          anchor="top end"
          hideArrow
          hideFocusBorder
          popupWidthFitContent
        />
      </PortalGroup>

      {/* Picture-in-Picture */}
      {document.pictureInPictureEnabled && (
        <button
          type="button"
          onClick={togglePiP}
          aria-label={isPiP ? 'Exit picture-in-picture' : 'Picture-in-picture'}
          title={isPiP ? 'Exit picture-in-picture' : 'Picture-in-picture'}
          className={cn(
            'odin-text-white hover:odin-text-gray-300',
            isPiP && 'odin-text-blue-400',
          )}
        >
          <ObjectGroupIcon className="odin-h-4 odin-w-4" />
        </button>
      )}

      {/* Fullscreen */}
      <button
        type="button"
        onClick={toggleFullscreen}
        aria-label={
          document.fullscreenElement ? 'Exit fullscreen' : 'Fullscreen'
        }
        title={document.fullscreenElement ? 'Exit fullscreen' : 'Fullscreen'}
        className="odin-text-white hover:odin-text-gray-300"
      >
        <ExpandIcon className="odin-h-4 odin-w-4" />
      </button>
    </div>
  );
}
